import React, { useCallback, useMemo } from "react";
import {
  Select,
  SelectProps,
  Typography,
  Box,
  useTheme,
  FormControl,
  MenuItem,
  FormControlProps,
  FormHelperText,
  Checkbox,
  ListItemText,
  Chip,
  SelectChangeEvent,
} from "@mui/material";

import CloseIcon from "../../icons/CloseIcon";
import ChevronDownIcon from "../../icons/ChevronDownIcon";
import ChevronUpIcon from "../../icons/ChevronUpIcon";

export interface SelectOption {
  id?: string;
  value: string;
  label: string;
}

interface CoreSelectProps extends Omit<
  SelectProps,
  "label" | "children" | "variant" | "onChange"
> {
  label?: string;
  options: SelectOption[];
  formControlProps?: FormControlProps;
  placeholder?: string;
  variant?: "standard" | "outlined" | "filled" | "underline";
  helperText?: string;
  enableClear?: boolean;
  onChange?: (event: SelectChangeEvent<unknown>) => void;
}

const CoreSelect: React.FC<CoreSelectProps> = ({
  label,
  value,
  options,
  formControlProps,
  placeholder,
  variant = "standard",
  helperText,
  enableClear = false,
  multiple,
  onChange,
  IconComponent: userIconComponent,
  sx: selectSx,
  name,
  ...selectProps
}) => {
  const theme = useTheme();
  const { sx: formControlSx, ...restFormControlProps } = formControlProps ?? {};

  const isUnderlineVariant = variant === "underline";
  const dataVariant = isUnderlineVariant ? "underline" : undefined;

  const hasValue = multiple
    ? Array.isArray(value) && value.length > 0
    : value !== undefined && value !== null && value !== "";

  const showClearButton = enableClear && hasValue;

  const handleClear = useCallback(
    (event: React.SyntheticEvent) => {
      event.stopPropagation();
      event.preventDefault();

      onChange?.({
        target: { value: multiple ? [] : "", name },
      } as SelectChangeEvent<unknown>);
    },
    [multiple, name, onChange],
  );

  const iconSlotSize = 18;

  const selectIconSx = useMemo(
    () => ({
      width: enableClear ? iconSlotSize * 2 + 8 : iconSlotSize,
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 0.5,
      color: theme.palette.text.secondary,
      pointerEvents: "none" as const,
    }),
    [enableClear, theme.palette.text.secondary],
  );

  const SelectIcon = useMemo(() => {
    const chevronSlotSx = {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: iconSlotSize,
      height: iconSlotSize,
      flexShrink: 0,
      color: "inherit",
      "& svg": { width: 10, height: 6, display: "block" },
    } as const;

    const Component = ({ className }: { className?: string }) => {
      const isOpen = className?.includes("MuiSelect-iconOpen");

      return (
        <Box className={className} sx={selectIconSx}>
          {enableClear ? (
            showClearButton ? (
              <Box
                component="span"
                role="button"
                tabIndex={0}
                aria-label="clear selection"
                onClick={handleClear}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    handleClear(event);
                  }
                }}
                sx={{
                  pointerEvents: "auto",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: iconSlotSize,
                  height: iconSlotSize,
                  flexShrink: 0,
                  borderRadius: "50%",
                  color: "inherit",
                  cursor: "pointer",
                  "&:hover": {
                    color: theme.palette.text.primary,
                    bgcolor: "action.hover",
                  },
                }}
              >
                <CloseIcon size={12} />
              </Box>
            ) : (
              <Box
                sx={{
                  width: iconSlotSize,
                  height: iconSlotSize,
                  flexShrink: 0,
                }}
              />
            )
          ) : null}
          <Box component="span" sx={chevronSlotSx}>
            {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </Box>
        </Box>
      );
    };
    return Component;
  }, [
    enableClear,
    handleClear,
    selectIconSx,
    showClearButton,
    theme.palette.text.primary,
  ]);

  const renderValue = (selected: unknown) => {
    if (multiple) {
      const selectedArray = selected as string[];
      if (!selectedArray || selectedArray.length === 0) {
        return (
          <span
            style={{
              color: isUnderlineVariant
                ? theme.palette.text.secondary
                : "#6B7280",
            }}
          >
            {placeholder || ""}
          </span>
        );
      }
      return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {selectedArray.map((val) => {
            const option = options.find((opt) => opt.value === val);
            return (
              <Chip
                key={val}
                label={option?.label || val}
                size="small"
                sx={{ height: 24 }}
              />
            );
          })}
        </Box>
      );
    }

    if (!selected || selected === "") {
      return (
        <span
          style={{
            color: isUnderlineVariant
              ? theme.palette.text.secondary
              : "#6B7280",
          }}
        >
          {placeholder || ""}
        </span>
      );
    }

    const selectedOption = options.find((opt) => opt.value === selected);
    return selectedOption ? selectedOption.label : String(selected);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      {label && (
        <Typography
          variant="body2"
          component="label"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 700,
            mb: 0.5,
            fontSize: "0.875rem",
          }}
        >
          {label}
        </Typography>
      )}

      <FormControl
        fullWidth
        variant="standard"
        data-variant={dataVariant}
        sx={formControlSx}
        {...restFormControlProps}
      >
        <Select
          data-variant={dataVariant}
          multiple={multiple}
          IconComponent={enableClear ? SelectIcon : userIconComponent}
          sx={{
            "& .MuiSelect-select": {
              height: "100%",
              display: "flex",
              alignItems: "center",
              ...(enableClear && { paddingRight: "48px !important" }),
            },
            ...(enableClear && {
              "& .MuiSelect-icon": {
                right: 10,
                top: "50%",
                marginTop: 0,
                transform: "translateY(-50%)",
                color: theme.palette.text.secondary,
              },
              "& .MuiSelect-iconOpen": {
                transform: "translateY(-50%)",
              },
            }),
            ...selectSx,
          }}
          renderValue={renderValue}
          onChange={onChange}
          name={name}
          {...selectProps}
          value={multiple ? value || [] : value || ""}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {multiple && (
                <Checkbox
                  checked={Array.isArray(value) && value.includes(option.value)}
                />
              )}
              {multiple ? (
                <ListItemText primary={option.label} />
              ) : (
                option.label
              )}
            </MenuItem>
          ))}
        </Select>

        {helperText && (
          <FormHelperText sx={{ mt: 0.5, mx: 0 }}>{helperText}</FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};

export default CoreSelect;
